import { QueryClient } from '@tanstack/react-query'

// Cache Strategy - Baseado na frequência de mudança dos dados
export const CACHE_TIMES = {
  // Dados que mudam muito - SEM CACHE
  NOTIFICATIONS: 30 * 1000,          // 30 segundos
  CLAIMS_LIST: 0,                    // 🔥 SEM CACHE - sempre buscar dados frescos
  DASHBOARD_STATS: 2 * 60 * 1000,    // 2 minutos
  CLAIM_NOTES: 1 * 60 * 1000,        // 1 minuto
  
  // Dados que mudam moderadamente - cache médio
  CLAIM_DETAILS: 5 * 60 * 1000,      // 5 minutos
  USERS_LIST: 10 * 60 * 1000,        // 10 minutos
  SEARCH_RESULTS: 3 * 60 * 1000,     // 3 minutos
  
  // Dados que mudam pouco - cache longo
  PRODUCTS: 30 * 60 * 1000,          // 30 minutos
  COVERAGE_TYPES: 30 * 60 * 1000,    // 30 minutos
  USER_PROFILE: 15 * 60 * 1000,      // 15 minutos
  
  // Dados quase estáticos - cache muito longo
  PERMISSIONS: 60 * 60 * 1000,       // 1 hora
  CONSTANTS: 24 * 60 * 60 * 1000,    // 24 horas
} as const

// Configuração do QueryClient
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache Strategy
      staleTime: 2 * 60 * 1000,      // 2 minutos - dados considerados fresh
      gcTime: 10 * 60 * 1000,        // 10 minutos - garbage collection
      
      // Error Handling
      retry: 3,                      // 3 tentativas
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Background Updates
      refetchOnWindowFocus: true,    // Refetch ao focar janela
      refetchOnReconnect: true,      // Refetch ao reconectar
      
      // Network Mode
      networkMode: 'online',         // Apenas online
      
      // Configurações de performance
      refetchIntervalInBackground: false,  // Não refetch em background por padrão
      
      // Configurações de UX
      notifyOnChangeProps: 'all',    // Notificar mudanças para otimizar renders
    },
    mutations: {
      // Mutation Defaults
      retry: 1,                      // 1 tentativa para mutations
      networkMode: 'online',
      
      // Error handling para mutations
      onError: (error) => {
        console.error('Mutation error:', error)
        // Aqui poderia integrar com sistema de notificações
      },
    },
  },
})

// Configuração de refetch intervals para dados críticos
export const REFETCH_INTERVALS = {
  dashboard: 5 * 60 * 1000,         // 5 minutos
  notifications: 30 * 1000,         // 30 segundos
  claims: 2 * 60 * 1000,           // 2 minutos
} as const

// Configuração de retry baseado no tipo de erro
export const getRetryConfig = (error: any) => {
  // Não retry para erros de autenticação
  if (error?.status === 401 || error?.status === 403) {
    return false
  }
  
  // Não retry para erros de validação
  if (error?.status >= 400 && error?.status < 500) {
    return false
  }
  
  // Retry para erros de servidor
  return true
}

// Configuração de stale time baseado no tipo de query
export const getStaleTime = (queryType: keyof typeof CACHE_TIMES): number => {
  return CACHE_TIMES[queryType] || CACHE_TIMES.CLAIM_DETAILS
}

// Helper para configurar queries específicas
export const createQueryConfig = (queryType: keyof typeof CACHE_TIMES) => ({
  staleTime: getStaleTime(queryType),
  gcTime: 10 * 60 * 1000,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
  retry: getRetryConfig,
})

// Configuração para queries que precisam de background refresh
export const createBackgroundRefreshConfig = (
  queryType: keyof typeof CACHE_TIMES,
  interval?: number
) => ({
  ...createQueryConfig(queryType),
  refetchInterval: interval || REFETCH_INTERVALS.dashboard,
  refetchIntervalInBackground: false,
})

// Configuração para queries sensíveis ao tempo
export const createTimeBasedQueryConfig = (queryType: keyof typeof CACHE_TIMES) => ({
  ...createQueryConfig(queryType),
  refetchOnWindowFocus: true,
  refetchOnMount: true,
  refetchIntervalInBackground: false,
})

// Configuração para queries de cache longo
export const createLongCacheConfig = (queryType: keyof typeof CACHE_TIMES) => ({
  ...createQueryConfig(queryType),
  refetchOnWindowFocus: false,
  refetchOnMount: 'always' as const,
  refetchOnReconnect: false,
})

// Configuração para infinite queries
export const createInfiniteQueryConfig = (queryType: keyof typeof CACHE_TIMES) => ({
  ...createQueryConfig(queryType),
  getNextPageParam: (lastPage: any) => {
    return lastPage?.hasNextPage ? lastPage.nextPage : undefined
  },
  getPreviousPageParam: (firstPage: any) => {
    return firstPage?.hasPreviousPage ? firstPage.previousPage : undefined
  },
  maxPages: 10, // Limitar páginas em cache
})

// Configuração para queries de search
export const createSearchQueryConfig = () => ({
  ...createQueryConfig('SEARCH_RESULTS'),
  enabled: false, // Desabilitado por padrão, será habilitado via query
  refetchOnWindowFocus: false,
  refetchOnMount: false,
})

// Configuração para queries offline
export const createOfflineQueryConfig = (queryType: keyof typeof CACHE_TIMES) => ({
  ...createQueryConfig(queryType),
  networkMode: 'offlineFirst' as const,
  retry: false,
})

// Configuração para queries com optimistic updates
export const createOptimisticQueryConfig = (queryType: keyof typeof CACHE_TIMES) => ({
  ...createQueryConfig(queryType),
  // Configurações específicas para optimistic updates
  refetchOnWindowFocus: false, // Evitar refetch durante updates
  refetchOnMount: false,
})

// Configuração para queries de tempo real
export const createRealtimeQueryConfig = (queryType: keyof typeof CACHE_TIMES) => ({
  ...createQueryConfig(queryType),
  refetchInterval: false, // Desabilitar refetch automático
  refetchOnWindowFocus: false, // Realtime já mantém atualizado
  refetchOnReconnect: true, // Apenas ao reconectar
})

// Export do cliente configurado
export default queryClient 